import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import config from '../../config';
import styles from './CustomerOrders.module.css';

const CustomerOrders = () => {
  const { user, token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedOrders, setCollapsedOrders] = useState([]);

  useEffect(() => {
    if (!user || !token) return;

    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${config.backendUrl}/api/orders/my-orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const activeOrders = res.data.filter(order => order.status !== 'Confirmed Delivered');
        setOrders(activeOrders);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [user, token]);

  const handleConfirmReceived = async (orderId) => {
    try {
      await axios.put(
        `${config.backendUrl}/api/orders/${orderId}/confirm-delivery`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderId));
    } catch (err) {
    }
  };

  const getOrderButtonStatus = (order) => {
    const statuses = order.subOrders.map((sub) => sub.status);
    const uniqueStatuses = [...new Set(statuses)];

    if (uniqueStatuses.length === 1) {
      return uniqueStatuses[0];
    }

    const statusPriority = ['Pending', 'OrderReceived', 'Preparing', 'ReadyForPickup', 'PickedUp', 'OutForDelivery', 'Delivered'];
    return statusPriority.find((status) => statuses.includes(status)) || 'Processing';
  };

  const handleToggleCollapse = (orderId) => {
    setCollapsedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleRemoveOrder = (orderId) => {
    setOrders((prevOrders) => prevOrders.filter((order) => order._id !== orderId));
  };

  if (loading) return (
    <div className={styles.container}>
      <div className={styles.skeletonGrid}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className={styles.skeletonCard}>
            <div className={`${styles.skeletonBar} ${styles.wide}`}></div>
            <div className={styles.skeletonSpacer}></div>
            <div className={styles.skeletonBar}></div>
            <div className={styles.skeletonSpacer}></div>
            <div className={`${styles.skeletonBar} ${styles.thin}`}></div>
            <div className={styles.skeletonSpacer}></div>
            <div className={`${styles.skeletonBar} ${styles.thin}`}></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Your Orders</h2>
      {orders.length === 0 && <p className={styles.noOrders}>No orders found.</p>}

      <div className={styles.ordersGrid}>
        {orders.map((order) => {
          const isCollapsed = collapsedOrders.includes(order._id);

          return (
            <div key={order._id} className={styles.orderCard}>
              <h3 className={styles.orderId}>Order ID: {order.orderId}</h3>

              {isCollapsed ? (
                <div className={styles.collapsedActions}>
                  <button
                    onClick={() => handleToggleCollapse(order._id)}
                    className={`${styles.button} ${styles.viewButton}`}
                  >
                    View Order
                  </button>
                  <button
                    onClick={() => handleRemoveOrder(order._id)}
                    className={`${styles.button} ${styles.removeButton}`}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <p className={styles.orderInfo}>Created At: {new Date(order.createdAt).toLocaleString()}</p>
                  <p className={styles.orderInfo}>Total: KES {order.total}</p>
                  <p className={styles.orderInfo}>Payment Method: {order.paymentMethod}</p>
                  <p className={styles.orderInfo}>Payment Status: {order.paymentStatus}</p>
                  <p className={styles.orderInfo}>
                    Delivery: {order.delivery.town}, {order.delivery.location} ({order.delivery.option})
                  </p>

                  <h4 className={styles.subOrdersTitle}>Sub-Orders (Grouped by Shop):</h4>
                  <ul className={styles.subOrdersList}>
                    {order.subOrders.map((sub) => (
                      <li key={sub._id} className={styles.subOrder}>
                        <h5 className={styles.shopName}>Shop: {sub.shop?.businessName || 'Unknown Shop'}</h5>
                        <ul className={styles.itemsList}>
                          {sub.items.map((item) => (
                            <li key={item._id} className={styles.item}>
                              Product: {item.product?.name} — Qty: {item.quantity} — Price: KES {item.price}
                            </li>
                          ))}
                        </ul>
                        <p className={styles.subOrderTotal}>
                          <strong>Total:</strong> KES {sub.total}
                        </p>
                        <button disabled className={`${styles.button} ${styles.disabledButton}`}>
                          {sub.status}
                        </button>
                        {/* <p className={styles.subOrderTotal}>
                          <strong>Total:</strong> KES {sub.total}
                        </p> */}
                      </li>
                    ))}
                  </ul>
                  
                  {(
                    order.status === 'Delivered' ||
                    order.status === 'ReadyForPickup' ||
                    (order.status === 'PickedUp' && order.delivery.option === 'own')
                  )
                    ? (
                      <button
                        onClick={() => handleConfirmReceived(order._id)}
                        className={`${styles.button} ${styles.confirmButton}`}
                      >
                        Confirm Received
                      </button>
                    ) : (
                      <button disabled className={`${styles.button} ${styles.disabledButton}`}>
                        {getOrderButtonStatus(order)}
                      </button>
                    )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerOrders;
